// AddressAutocomplete.tsx (updated for the 2025 Autocomplete API)
import React, { useEffect, useRef, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import "./AddressAutocomplete.scss";

type AddressParts = {
    street?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    lat?: number;
    lng?: number;
    formattedAddress?: string;
    placeId?: string;
};

type Props = {
    onSelect: (a: AddressParts) => void;
    placeholder?: string;
    /** e.g. 'es' (Spain). Pass undefined to allow all countries. */
    country?: string | string[];
    /** Minimum chars before querying (default 3). */
    minLength?: number;
    /** Debounce in ms (default 300). */
    debounceMs?: number;
    /** Optional pre-filled text (Formik’s current address). */
    initialText?: string;
};

export default function AddressAutocomplete({
                                                onSelect,
                                                placeholder = "Enter an address",
                                                country = "es",
                                                minLength = 3,
                                                debounceMs = 300,
                                                initialText = "",
                                            }: Props) {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: "AIzaSyAvvyTFv36BTAbDSTWguk299eLTBflBiwE",
        libraries: ["places"],
        version: "weekly",
    });

    const [query, setQuery] = useState(initialText);
    const [preds, setPreds] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [isFocused, setIsFocused] = useState(false);

    const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const debounceTimer = useRef<number | null>(null);

    useEffect(() => {
        if (!isLoaded) return;
        if (!sessionToken.current) {
            sessionToken.current = new google.maps.places.AutocompleteSessionToken();
        }
    }, [isLoaded]);

    // Debounced prediction fetch using the new API
    useEffect(() => {
        if (!isLoaded || !isFocused) return;

        const trimmed = query.trim();
        if (trimmed.length < minLength) {
            setPreds([]);
            return;
        }

        if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
        debounceTimer.current = window.setTimeout(async () => {
            try {
                const req: google.maps.places.AutocompleteRequest = {
                    input: trimmed,
                    sessionToken: sessionToken.current ?? undefined,
                    includedPrimaryTypes: [
                        "street_address", "premise", "subpremise",
                        "locality", "administrative_area_level_1",
                    ],
                    // Limit by country/region codes if provided
                    includedRegionCodes: typeof country === "string" ? [country] : country,
                };

                // NOTE: returns { suggestions: AutocompleteSuggestion[] }
                const { suggestions } =
                    await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);

                setPreds(suggestions ?? []);
            } catch (err) {
                console.error("Autocomplete error", err);
                setPreds([]);
            }
        }, debounceMs);

        return () => {
            if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
        };
    }, [query, isFocused, isLoaded, debounceMs, country, minLength]);

    const selectPrediction = async (s: google.maps.places.AutocompleteSuggestion) => {
        try {
            const pp = s.placePrediction;
            if (!pp) return;

            // Convert prediction to Place and fetch only needed fields
            const place = pp.toPlace();
            await place.fetchFields({
                fields: ["addressComponents", "location", "formattedAddress", "id"],
            });

            const comps = place.addressComponents ?? [];
            const streetComp = comps.find(c => c.types.includes("route"));
            const houseNumComp = comps.find(c => c.types.includes("street_number"));
            const postalComp = comps.find(c => c.types.includes("postal_code"));
            const cityComp = comps.find(
                (c) =>
                    c.types.includes("locality") ||
                    c.types.includes("administrative_area_level_2") ||
                    c.types.includes("administrative_area_level_1")
            );
            const countryComp = comps.find((c) => c.types.includes("country"));

            const payload: AddressParts = {
                street: streetComp?.longText || "",
                houseNumber: houseNumComp?.longText || "",
                postalCode: postalComp?.longText || "",
                city: cityComp?.longText || "",
                country: countryComp?.longText || "ES",
                lat: place.location?.lat(),
                lng: place.location?.lng(),
                formattedAddress: place.formattedAddress ?? pp.text?.text,
                placeId: place.id,
            };

            setQuery(payload.formattedAddress || pp.text?.text || "");
            setPreds([]);
            onSelect(payload);

            // End session
            sessionToken.current = null;
        } catch (err) {
            console.error("Error fetching place details", err);
        }
    };

    if (loadError) return <div>Error loading Google Maps</div>;
    if (!isLoaded) return null;

    const renderLabel = (s: google.maps.places.AutocompleteSuggestion) => {
        const text = s.placePrediction?.text?.text;
        const main = s.placePrediction?.mainText?.text;
        const secondary = s.placePrediction?.secondaryText?.text;
        return text || (main && secondary ? `${main} — ${secondary}` : main || secondary || "");
    };

    return (
        <div className="relative">
            <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                    setIsFocused(true);
                    if (!sessionToken.current) {
                        sessionToken.current = new google.maps.places.AutocompleteSessionToken();
                    }
                }}
                onBlur={() => window.setTimeout(() => setIsFocused(false), 150)}
                placeholder={placeholder}
                className="form-control px-4 py-1 text-xs mb-3"
                autoComplete="off"
            />

            {isFocused && preds.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 max-h-64 overflow-auto autocomplete-results">
                    {preds.map((s, i) => (
                        <li
                            key={s.placePrediction?.placeId ?? i}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onMouseDown={() => selectPrediction(s)}
                        >
                            {renderLabel(s)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
